import React from 'react';
import dynamic from 'next/dynamic';
import {
  NotesPanelSkeleton,
  EntityInfoPanelSkeleton,
  NotebookPanelSkeleton,
  AOFCollectionsSkeleton,
  EditorWrapperSkeleton,
  OpenNotesPanelSkeleton,
  NoteHistoryPanelSkeleton,
} from '../components/PanelLoadingSkeleton';
import { prefetchPanelComponent } from '../utils/prefetchUtils';

// Critical panels - eager load for instant access (Notes and Editor are always visible)
// Using ssr: false and no loading state for instant rendering
const Notes = dynamic(() => import('../views/panels/NotesPanel'), {
  ssr: false,
});

const EditorWrapper = dynamic(() => import('../views/editor/EditorWrapper'), {
  ssr: false,
});

// Secondary panels - lazy load but prefetch on hover
const EntityInfoPanel = dynamic(
  () => import('../views/panels/EntityInfoPanel'),
  {
    loading: () => <EntityInfoPanelSkeleton />,
    ssr: false,
  }
);
const NotebookPanel = dynamic(
  () => import('../views/panels/NotebookPanel'),
  {
    loading: () => <NotebookPanelSkeleton />,
    ssr: false,
  }
);
const AOFCollections = dynamic(
  () => import('../views/panels/AOFCollections'),
  {
    loading: () => <AOFCollectionsSkeleton />,
    ssr: false,
  }
);
const OpenNotesPanel = dynamic(
  () => import('../views/editor/OpenNotesPanel'),
  {
    loading: () => <OpenNotesPanelSkeleton />,
    ssr: false,
  }
);
const NoteHistoryPanel = dynamic(
  () => import('../views/panels/NoteHistoryPanel'),
  {
    loading: () => <NoteHistoryPanelSkeleton />,
    ssr: false,
  }
);

// Export prefetch functions for tab hover
export const prefetchNotes = () => prefetchPanelComponent('notes');
export const prefetchEditor = () => prefetchPanelComponent('editorWrapper');
export const prefetchInfo = () => prefetchPanelComponent('info');
export const prefetchNotebooks = () => prefetchPanelComponent('notebooks');
export const prefetchAOF = () => prefetchPanelComponent('aofCollections');
export const prefetchOpenNotes = () => prefetchPanelComponent('openNotesPanel');
export const prefetchHistory = () => prefetchPanelComponent('history');

export const model = {
  global: {
    tabEnableClose: false,
    splitterSize: 6,
    tabDragSpeed: '0.1',
  },
  layout: {
    type: 'row',
    weight: 100,
    children: [
      {
        type: 'tabset',
        weight: 20,
        selected: 0,
        tabStripHeight: 52,
        children: [
          {
            type: 'tab',
            name: 'Notes',
            component: 'notes',
          },
          {
            type: 'tab',
            name: 'Areas of focus',
            component: 'aofCollections',
          },
          {
            type: 'tab',
            name: 'Notebooks',
            component: 'notebooks',
          },
          {
            type: 'tab',
            name: 'Open Notes',
            component: 'openNotesPanel',
          },
          {
            type: 'tab',
            name: 'Info',
            component: 'info',
          },
          {
            type: 'tab',
            name: 'History',
            component: 'history',
          },
          // {
          //   type: 'tab',
          //   name: 'Quick Note',
          //   component: 'quickNote',
          // },
        ],
      },
      {
        type: 'tabset',
        weight: 80,
        selected: 0,
        enableTabStrip: false,
        children: [
          {
            type: 'tab',
            name: 'Editor',
            component: 'editorWrapper',
          },
        ],
      },
    ],
  },
};

export const modelSmall = {
  global: {
    tabEnableClose: false,
    splitterSize: 6,
    tabDragSpeed: '0.1',
  },
  layout: {
    type: 'row',
    weight: 100,
    children: [
      {
        type: 'tabset',
        weight: 20,
        selected: 0,
        tabStripHeight: 52,
        children: [
          // {
          //   type: 'tab',
          //   name: 'Quick Note',
          //   component: 'quickNote'
          // },
          {
            type: 'tab',
            name: 'Editor',
            component: 'editorWrapper',
          },
          {
            type: 'tab',
            name: 'Open Notes',
            component: 'openNotesPanel',
          },
          {
            type: 'tab',
            name: 'Notes',
            component: 'notes',
          },
          {
            type: 'tab',
            name: 'Notebooks',
            component: 'notebooks',
          },
          {
            type: 'tab',
            name: 'Areas of focus',
            component: 'aofCollections',
          },
          {
            type: 'tab',
            name: 'Info',
            component: 'info',
          },
          {
            type: 'tab',
            name: 'History',
            component: 'history',
          },
        ],
      },
    ],
  },
};

export const getFactory = (node) => {
  const component = node.getComponent();

  switch (component) {
    case 'text': {
      return <div className="panel">Panel {node.getName()}</div>;
    }
    // case 'notes': {
    //   return (
    //     <div
    //       className="panel"
    //       onClick={() => {
    //         console.log('notes clicked');
    //       }}
    //     >
    //       Panel {node.getName()}
    //     </div>
    //   );
    // }
    // case 'info': {
    //   return <div className="panel">Panel {node.getName()}</div>;
    // }
    // case 'notebooks': {
    //   return <div className="panel">Panel {node.getName()}</div>;
    // }
    // case 'aofCollections': {
    //   return <div className="panel">Panel {node.getName()}</div>;
    // }
    // case 'editorWrapper': {
    //   return <div className="panel">Panel {node.getName()}</div>;
    // }
    // case 'openNotesPanel': {
    //   return <div className="panel">Panel {node.getName()}</div>;
    // }
    case 'quickNote': {
      return <div className="panel">Panel {node.getName()}</div>;
    }
    case 'notes': {
      return <Notes />;
    }
    case 'info': {
      return <EntityInfoPanel />;
    }
    case 'notebooks': {
      return <NotebookPanel />;
    }
    case 'aofCollections': {
      return <AOFCollections />;
    }
    case 'editorWrapper': {
      return <EditorWrapper />;
    }
    // Backwards compatibility: saved layouts may still use the old component name
    case 'noteTabsMobile':
    case 'openNotesPanel': {
      return <OpenNotesPanel />;
    }
    case 'history': {
      return <NoteHistoryPanel />;
    }
    // case 'quickNote': {
    //   return <QuickNotes />;
    // }

    default: {
      return <div>Unknown component: {component}</div>;
    }
  }
};
